import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PER_PAGE, Pager } from "@/components/Pager";
import { api, downloadFile, errorMessage } from "@/lib/api";
import { formatDate, money } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const Stat = ({ label, value, hint, testId }) => (
  <div className="surface p-5" data-testid={testId}>
    <p className="label-caps">{label}</p>
    <p className="num mt-3 text-2xl text-slate-900">{value}</p>
    {hint ? <p className="mt-1.5 text-[11px] text-slate-500">{hint}</p> : null}
  </div>
);

export default function TdsRegister() {
  const { currency } = useAuth();
  const [data, setData] = useState(null);
  const [fy, setFy] = useState("");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get("/reports/tds", { params: fy ? { fy } : {} });
      setData(res);
      setFy(res.fy || "");
    } catch (error) {
      toast.error(errorMessage(error, "Could not load the TDS register"));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fy]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fy]);

  const symbol = data?.currency_symbol || currency;

  const exportCsv = async (scope) => {
    try {
      await downloadFile(
        `/exports/tds.csv?fy=${encodeURIComponent(scope)}`,
        scope === "all" ? "tds_register_all_years.csv" : `tds_register_${scope}.csv`,
      );
      toast.success("TDS register exported");
    } catch (error) {
      toast.error(errorMessage(error, "Export failed"));
    }
  };

  if (loading && !data) {
    return (
      <div className="flex h-64 items-center justify-center" data-testid="tds-loading">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      </div>
    );
  }

  const rows = data?.rows || [];
  const years = data?.years || [];
  const totals = data?.totals || { tds: 0, inward: 0, credited: 0, receipts: 0 };

  return (
    <div className="space-y-8" data-testid="tds-page">
      <header className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="label-caps">Accounts</p>
          <h1 className="mt-3 font-serif text-3xl tracking-tight text-slate-900 lg:text-4xl">
            TDS register
          </h1>
          <p className="mt-3 max-w-xl text-sm text-slate-600">
            Every rupee deducted at source, financial year by financial year, ready to be tallied
            against Form 26AS. Years run 1 April to 31 March and follow the date the payment was
            received.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => exportCsv(fy || "all")}
            data-testid="export-tds-year-button"
            className="inline-flex items-center gap-2 border border-slate-300 bg-white px-5 py-2.5 text-[13px] font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-50"
          >
            <Download className="h-3.5 w-3.5" strokeWidth={1.8} />
            {fy ? `Export ${fy}` : "Export CSV"}
          </button>
          <button
            type="button"
            onClick={() => exportCsv("all")}
            data-testid="export-tds-all-button"
            className="inline-flex items-center gap-2 border border-slate-300 bg-white px-5 py-2.5 text-[13px] font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-50"
          >
            All years
          </button>
        </div>
      </header>

      {years.length === 0 ? (
        <div className="surface p-8 text-center" data-testid="tds-empty">
          <p className="font-serif text-xl text-slate-900">No TDS recorded yet</p>
          <p className="mt-2 text-[13px] text-slate-600">
            Tick “TDS deducted” while recording a payment and it will appear here, year by year.
          </p>
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Stat
              label={`TDS deducted · ${fy}`}
              value={money(totals.tds, symbol)}
              hint="Tally this against Form 26AS"
              testId="tds-stat-deducted"
            />
            <Stat
              label="Received in bank"
              value={money(totals.inward, symbol)}
              testId="tds-stat-inward"
            />
            <Stat
              label="Credited to fee notes"
              value={money(totals.credited, symbol)}
              hint="Bank + TDS"
              testId="tds-stat-credited"
            />
            <Stat
              label="Receipts with TDS"
              value={String(totals.receipts)}
              testId="tds-stat-count"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2" data-testid="tds-year-tabs">
            {years.map((year) => (
              <button
                key={year}
                type="button"
                onClick={() => setFy(year)}
                data-testid={`tds-year-${year}`}                className={`num border px-3 py-1.5 text-[12px] transition-colors duration-150 ${
                  year === fy
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-slate-300 bg-white text-slate-600 hover:border-slate-500"
                }`}
              >
                {year}
              </button>
            ))}
          </div>

          <div className="surface p-6" data-testid="tds-year-summary">
            <h2 className="font-serif text-xl text-slate-900">Year by year</h2>
            <div className="mt-5 max-w-full overflow-x-auto">
              <table className="w-full min-w-[560px] text-left">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="label-caps px-3 py-2.5">Financial year</th>
                    <th className="label-caps px-3 py-2.5 text-right">Receipts</th>
                    <th className="label-caps px-3 py-2.5 text-right">Received in bank</th>
                    <th className="label-caps px-3 py-2.5 text-right">TDS deducted</th>
                    <th className="label-caps px-3 py-2.5 text-right">Credited</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(data?.all_years || []).map((y) => (
                    <tr
                      key={y.fy}
                      className={y.fy === fy ? "bg-slate-50" : ""}
                      data-testid={`tds-summary-row-${y.fy}`}
                    >
                      <td className="num px-3 py-3 text-[13px] text-slate-900">{y.fy}</td>
                      <td className="num px-3 py-3 text-right text-[13px] text-slate-600">
                        {y.receipts}
                      </td>
                      <td className="num px-3 py-3 text-right text-[13px] text-slate-600">
                        {money(y.inward, symbol)}
                      </td>
                      <td className="num px-3 py-3 text-right text-[13px] font-semibold text-slate-900">
                        {money(y.tds, symbol)}
                      </td>
                      <td className="num px-3 py-3 text-right text-[13px] text-slate-600">
                        {money(y.credited, symbol)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="surface p-6" data-testid="tds-detail">
            <h2 className="font-serif text-xl text-slate-900">Deduction detail · {fy}</h2>
            <div className="mt-5 max-w-full overflow-x-auto">
              <table className="w-full min-w-[860px] text-left">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="label-caps px-3 py-2.5">Received on</th>
                    <th className="label-caps px-3 py-2.5">Deductor</th>
                    <th className="label-caps px-3 py-2.5">Fee note</th>
                    <th className="label-caps px-3 py-2.5">Receipt</th>
                    <th className="label-caps px-3 py-2.5 text-right">In bank</th>
                    <th className="label-caps px-3 py-2.5 text-right">TDS</th>
                    <th className="label-caps px-3 py-2.5 text-right">Credited</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.slice((page - 1) * PER_PAGE, page * PER_PAGE).map((r) => (
                    <tr key={r.payment_id} data-testid={`tds-row-${r.payment_id}`}>
                      <td className="num px-3 py-3 text-[13px] text-slate-600">
                        {formatDate(r.paid_on)}
                      </td>
                      <td className="max-w-[220px] px-3 py-3">
                        <p className="truncate text-[13px] text-slate-900">{r.deductor}</p>
                        <p className="truncate text-[11px] text-slate-400">
                          {[r.deductor_tax_id, r.matter_title].filter(Boolean).join(" · ")}
                        </p>
                      </td>
                      <td className="px-3 py-3">
                        <Link
                          to={`/invoices/${r.invoice_id}`}
                          className="num text-[13px] text-slate-900 underline decoration-slate-300 transition-colors duration-150 hover:text-brand-terracotta"
                        >
                          {r.invoice_number}
                        </Link>
                      </td>
                      <td className="num px-3 py-3 text-[12px] text-slate-500">
                        {r.receipt_no || "—"}
                        {r.reference ? <span className="block">{r.reference}</span> : null}
                      </td>
                      <td className="num px-3 py-3 text-right text-[13px] text-slate-600">
                        {money(r.inward_amount, symbol)}
                      </td>
                      <td className="num px-3 py-3 text-right text-[13px] font-semibold text-slate-900">
                        {money(r.tds_amount, symbol)}
                      </td>
                      <td className="num px-3 py-3 text-right text-[13px] text-slate-600">
                        {money(r.amount, symbol)}
                      </td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-3 py-6 text-center text-[13px] text-slate-500">
                        Nothing deducted in {fy}.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="mt-4">
              <Pager page={page} total={rows.length} onPage={setPage} testId="tds-pager" />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
